"use client"

import { useState, useEffect } from "react"
import api from "../../api/api"
import { useNavigate, useLocation } from "react-router-dom"
import { getTdnetUrl, getUs8kUrl } from "../../utils/commonUtils"
import Loading from "../../Loading"
import { useDashboardContext } from "../../../context/DashboardContext"
import { ExternalLink, Calendar } from "lucide-react"

const TimelyDisclosureTable = ({ secCode }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [timelyDisclosures, setTimelyDisclosures] = useState(null)
  const { state: dashboardState } = useDashboardContext()
  const [isJpx, setIsJpx] = useState(true)
  const [isMobile, setIsMobile] = useState(false)

  // 画面サイズの変更を検知
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkIfMobile()
    window.addEventListener("resize", checkIfMobile)

    return () => {
      window.removeEventListener("resize", checkIfMobile)
    }
  }, [])

  useEffect(() => {
    const fetchTimelyDisclosures = async () => {
      try {
        const response = await api.get(`${process.env.REACT_APP_API_URL}/chat/api/timely-disclosures/${secCode}/`, {})
        setTimelyDisclosures({
          page: response.data.page,
          num: response.data.num_of_data,
          data: response.data.results,
        })
      } catch (error) {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          // 未認証の場合、ログイン画面にリダイレクト
          navigate("/login", { state: { from: location.pathname } })
        } else {
          console.error("Error fetching timely disclosures:", error)
        }
      }
    }

    const getExchange = () => {
      if (dashboardState.companyInfo) {
        const companyInfo = dashboardState.companyInfo
        return companyInfo.exchangeshortname
      } else {
        return
      }
    }

    fetchTimelyDisclosures()
    const exchange = getExchange()
    if (exchange === "JPX") {
      setIsJpx(true)
    } else {
      setIsJpx(false)
    }
  }, [secCode, navigate, location.pathname, dashboardState.companyInfo])

  if (!timelyDisclosures) {
    return <Loading />
  }

  const toTerm = (term) => {
    if (term === "FY") {
      return "通期"
    } else if (["Q1", "Q2", "Q3", "Q4"].includes(term)) {
      return term
    }
  }

  const getDocumentTypes = (data) => {
    const cols = [
      "forecasts",
      "forecasts_revision",
      "stocks",
      "shareholders_meeting",
      "subsidiaries",
      "earnings_announcement",
      "earnings_presentation",
      "performance",
      "monthly_results",
      "personnel",
      "dividends",
      "organization",
      "litigation",
      "new_products",
      "change_in_shareholders",
      "mid_term_management_plan",
      "other",
    ]

    return cols.filter((col) => data[col]).map((col) => returnDocType(col))
  }

  const returnDocType = (docType) => {
    const docTypeMap = {
      forecasts: "業績予想",
      forecasts_revision: "業績・配当予想の修正",
      stocks: "株式",
      shareholders_meeting: "株主総会",
      subsidiaries: "子会社",
      earnings_announcement: "決算短信",
      earnings_presentation: "決算説明",
      performance: "業績関連",
      monthly_results: "月次データ",
      personnel: "人事",
      dividends: "配当",
      organization: "組織",
      litigation: "訴訟",
      new_products: "新商品",
      change_in_shareholders: "株主の異動",
      mid_term_management_plan: "中期経営計画",
      other: "その他",
    }

    return docTypeMap[docType] || docType
  }

  return (
    <div className="dashboard-row max-h-[600px] overflow-y-auto overflow-x-hidden" style={{ marginTop: 0 }}>
      <h3>適時開示情報</h3>

      {isMobile ? (
        // モバイル表示 - カードレイアウト
        <div className="space-y-4 max-h-[500px] overflow-y-auto overflow-x-hidden px-2">
          {timelyDisclosures.data.map((data) => (
            <div key={data.doc_id} className="border rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-1 text-sm font-medium text-gray-500 mb-2">
                <Calendar size={14} />
                <span>{data.date}</span>
              </div>

              <div className="flex flex-wrap gap-1 mb-2">
                {getDocumentTypes(data).map((type, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800"
                  >
                    {type}
                  </span>
                ))}
              </div>

              <a
                href={
                  isJpx
                    ? getTdnetUrl(data.date.replace(/-/g, ""), data.pdf_name)
                    : getUs8kUrl(secCode, data.date, data.pdf_name)
                }
                className="text-gray-600 hover:text-gray-800 font-medium flex items-start gap-1"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="flex-1">{data.title}</span>
                <ExternalLink size={16} className="flex-shrink-0 mt-1" />
              </a>
            </div>
          ))}
        </div>
      ) : (
        // デスクトップ表示 - テーブルレイアウト
        <div className="max-h-[500px] overflow-y-auto overflow-x-hidden">
          <table className="disclosure-table id-disclosure-table w-full">
            <thead>
              <tr>
                <th className="text-left">開示日時</th>
                <th className="text-left">種類</th>
                <th className="text-left">タイトル</th>
              </tr>
            </thead>
            <tbody>
              {timelyDisclosures.data.map((data) => (
                <tr key={data.doc_id}>
                  <td className="text-left">{data.date}</td>
                  <td className="text-left">
                    <div className="flex flex-wrap gap-1">
                      {getDocumentTypes(data).map((type, index) => (
                        <span
                          key={index}
                          className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800"
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="text-left">
                    <a
                      href={
                        isJpx
                          ? getTdnetUrl(data.date.replace(/-/g, ""), data.pdf_name)
                          : getUs8kUrl(secCode, data.date, data.pdf_name)
                      }
                      className="hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {data.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default TimelyDisclosureTable
